import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'

import TextInput from 'components/Common/Form/Input/TextInput'
import { useCreateTripItemFromNLPOption, useNLPItemCreationMessage, useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { FullTrip } from 'tripPlanner/types/common'
import { isRouteItem, startPlaceOfTripItem } from 'tripPlanner/components/TripMaps/utils'
import debounce from 'lodash.debounce'
import DropdownList from 'components/Luxkit/Dropdown/List/DropdownList'
import ListItem from 'components/Luxkit/List/ListItem'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import LineSpinnerIcon from 'components/Luxkit/Icons/line/LineSpinnerIcon'
import { ItemMetadata } from 'tripPlanner/models'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import Hr from 'components/Common/Hr'
import useArrowListNavigation from 'hooks/useArrowListNavigation'
import { SetOptional } from 'type-fest'
import { NLPResponseOption } from 'tripPlanner/types/nlp'
import { TripItem } from 'tripPlanner/types/tripItem'
import noop from 'lib/function/noop'
import LineNotesIcon from 'components/Luxkit/Icons/line/LineNotesIcon'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import * as Mapper from 'tripPlanner/mappers'
import ExampleFlipper from './ExampleFlipper'
import * as Analytics from 'analytics/analytics'

const SearchIcon = styled(LineSearchIcon)`
  margin-right: ${rem(8)};
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const LoadingIcon = styled(LineSpinnerIcon)`
  margin-right: ${rem(8)};
  animation: ${spin} 1s linear infinite;
`

const PreSpan = styled.span`
  white-space: pre-wrap;
`

const StyledExampleFlipper = styled(ExampleFlipper)`
  pointer-events: none;
  display: none;
  position: absolute;
  top: 50%;
  left: ${rem(14)};
  transform: translateY(-50%);
`

const InputWrapper = styled.div`
  position: relative;

  &:has(input:placeholder-shown) {
    ${StyledExampleFlipper} {
      display: block;
    }
  }
`

function determineBounds(trip: FullTrip) {
  const bounds = new google.maps.LatLngBounds()
  let hasGeoPlaces = false
  trip.items.forEach((item) => {
    if (item.type === 'NOTE' || isRouteItem(item)) {
      return
    }

    const startPlace = startPlaceOfTripItem(item)

    if (startPlace?.type === 'GEO') {
      bounds.extend(
        new google.maps.LatLng(startPlace.point.lat, startPlace.point.lng),
      )
      hasGeoPlaces = true
    }

    if ('endPlace' in item && item.endPlace?.type === 'GEO') {
      bounds.extend(
        new google.maps.LatLng(
          item.endPlace.point.lat,
          item.endPlace.point.lng,
        ),
      )
      hasGeoPlaces = true
    }
  })

  if (!hasGeoPlaces) {
    return undefined
  }

  // https://developers.google.com/maps/documentation/places/web-service/autocomplete#locationbias
  return `rectangle:${bounds.getSouthWest().lat()},${bounds.getSouthWest().lng()}|${bounds.getNorthEast().lat()},${bounds.getNorthEast().lng()}`
}

function getListItemId(index: number) {
  return `nlp-item-create-option-${index}`
}
const INPUT_ID = 'nlp-item-create-input'
const ADD_NOTE_ID = 'nlp-item-create-add-note'

interface Props {
  trip: FullTrip
  date?: string
  onItemCreated?: (item: TripItem) => void
  dropdownAutoHeight?: boolean
}

function NLPItemCreate({ trip, date, onItemCreated = noop, dropdownAutoHeight = false }: Props) {
  const inputElement = useRef<HTMLInputElement>(null)

  const locationBias = useMemo(() => determineBounds(trip), [trip])

  const [currentMessage, setCurrentMessage] = useState('')

  const interactionEventSent = useRef(false)

  const [resultsState, setResultsState] = useState<{
    options: Array<SetOptional<NLPResponseOption, 'parsedFields'>>,
    nlpResult?: any,
    sentTimestamp: number,
    error?: string,
    dropdownOpen: boolean,
  }>({
    options: [],
    sentTimestamp: 0,
    dropdownOpen: false,
  })

  const closeDropdown = useCallback(() => {
    setResultsState((prevState) => ({
      ...prevState,
      dropdownOpen: false,
    }))
  }, [])

  const openDropdown = useCallback(() => {
    setResultsState((prevState) => ({
      ...prevState,
      dropdownOpen: true,
    }))
  }, [])

  const { mutate: sendNLPMessage, isLoading: isSendingNLPMessage } = useNLPItemCreationMessage({
    tripId: trip.id,
    locationBias,
    date,
  },
  {
    onSuccess: ({ response, sentTimestamp }) => {
      setResultsState((prevState) => {
        if (sentTimestamp > prevState.sentTimestamp) {
          let responseMessages: Array<SetOptional<NLPResponseOption, 'parsedFields'>> = response.options
          if (responseMessages.length === 0 && response.noResultsMessage) {
            responseMessages = [{
              label: response.noResultsMessage,
            }]
          }
          return {
            options: responseMessages,
            sentTimestamp,
            error: undefined,
            dropdownOpen: true,
            nlpResult: response.nlpResult,
          }
        }
        return prevState
      })
    },
    onError: () => {
      setResultsState((prevState) => ({
        ...prevState,
        error: 'Something went wrong',
        dropdownOpen: true,
      }))
    },
  })

  const { mutate: createTripItemFromNLPOption, isLoading: isCreatingTripItemFromNLPOption } = useCreateTripItemFromNLPOption({
    onSuccess: (item) => {
      Analytics.trackClientEvent({
        subject: item.type,
        action: currentMessage,
        category: 'save_nlp_item',
        type: 'interaction',
      })
      closeDropdown()
      onItemCreated(Mapper.tripItem(item, trip.id))
    },
  })

  const { mutate: createTripItem, isLoading: isCreatingTripItem } = useCreateTripItem({
    onSuccess: (item) => {
      Analytics.trackClientEvent({
        subject: item.type,
        action: currentMessage,
        category: 'save_nlp_item',
        type: 'interaction',
      })
      closeDropdown()
      onItemCreated(item)
    },
  })

  const onInputChange = useMemo(() => debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputMessage = e.target.value
    setCurrentMessage(inputMessage)
    const sentTimestamp = Date.now()
    if (inputMessage.length > 0) {
      setResultsState((prevState) => {
        if (!prevState.dropdownOpen) {
          return {
            options: [],
            sentTimestamp: sentTimestamp - 1,
            dropdownOpen: true,
          }
        }
        return prevState
      })
      sendNLPMessage({ message: inputMessage, sentTimestamp })
      if (!interactionEventSent.current) {
        Analytics.trackClientEvent({
          subject: 'opened_nlp_input',
          action: 'opened_nlp_input',
          category: 'save_nlp_item',
          type: 'interaction',
        })
        interactionEventSent.current = true
      }
    } else {
      setResultsState({
        options: [],
        sentTimestamp,
        dropdownOpen: false,
      })
    }
  }, 300), [sendNLPMessage])

  const onInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && !resultsState.dropdownOpen && currentMessage.length > 0) {
      openDropdown()
    }
  }, [currentMessage.length, openDropdown, resultsState.dropdownOpen])

  const onInputFocus = useCallback(() => {
    if (!resultsState.dropdownOpen && currentMessage.length > 0) {
      openDropdown()
    }
  }, [currentMessage.length, openDropdown, resultsState.dropdownOpen])

  const onListItemClick = (item: NLPResponseOption) => {
    createTripItemFromNLPOption({ tripId: trip.id, item })
  }

  const onAddNoteClick = () => {
    createTripItem({
      tripId: trip.id,
      tripItem: {
        type: 'NOTE',
        notes: currentMessage,
        sourceType: 'NA',
        startDate: date,
      },
    })
  }

  const rootRef = useRef<HTMLDivElement>(null)

  useArrowListNavigation(
    [
      INPUT_ID,
      ...resultsState.options.map((_option, i) => getListItemId(i)),
      ADD_NOTE_ID,
    ],
    {
      enabled: resultsState.dropdownOpen,
      onRequestClose: closeDropdown,
      eventListenerTargetRef: rootRef,
      afterCloseFocusRef: inputElement,
    },
  )

  if (isCreatingTripItemFromNLPOption || isCreatingTripItem) {
    return <LoadingIndicator inline />
  }

  return (
    <div ref={rootRef}>
      <InputWrapper>
        <TextInput
          ref={inputElement}
          defaultValue={currentMessage}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
          onFocus={onInputFocus}
          endIcon={isSendingNLPMessage ? <LoadingIcon /> : <SearchIcon />}
          placeholder=" "
          noValidationSpacing
          id={INPUT_ID}
          autoComplete="off"
          disabled={isCreatingTripItemFromNLPOption || isCreatingTripItem}
        />
        <StyledExampleFlipper />
      </InputWrapper>
      <DropdownList
        size="fill-anchor"
        anchorRef={inputElement}
        triggerRef={inputElement}
        open={resultsState.dropdownOpen}
        onClose={closeDropdown}
        enableFlip={dropdownAutoHeight}
        autoHeight={dropdownAutoHeight}
      >
        {resultsState.options.length === 0 && isSendingNLPMessage && (
          <ListItem title={<LoadingAnimation />} />
        )}
        {resultsState.options.map((option, i) => {
          const { labelMarkdown, label, secondaryLabel, parsedFields } = option
          const { itemType } = parsedFields ?? {}
          const { icon: Icon } = ItemMetadata[itemType!] ?? {}
          return <ListItem
            key={i}
            startIcon={Icon && <Icon size="S" />}
            title={labelMarkdown ? <MarkdownRender content={labelMarkdown} /> : label}
            subtitle={<PreSpan>{secondaryLabel}</PreSpan>}
            id={getListItemId(i)}
            onClick={option.parsedFields ? () => onListItemClick(option) : undefined}
          />
        })}
        <Hr />
        <ListItem startIcon={<LineNotesIcon size="S" />} title={<>Add note &lsquo;{currentMessage}&rsquo;</>} id={ADD_NOTE_ID} onClick={onAddNoteClick} />
      </DropdownList>
    </div>
  )
}

export default NLPItemCreate
